import PropTypes from 'prop-types';
import React from 'react';
import omit from 'lodash.omit';
import { useTranslations, useLocale } from 'next-intl';
import { connect } from 'react-redux';

import track from '@helper/tracking';
import { getFileSize, getFileType } from '../../../helper/index';

import { LongArrowRight } from '../../icons/arrow/arrow';
import ArrowDown from '../../icons/arrowDown.svg';

import ConfirmationLayer from '../confirmation-layer/confirmationLayer';
import Headline from '../../atoms/headline/headline';
import Image from '../../atoms/image/image';
import MixedCopy from '../mixed-copy/mixedCopy';
import StyledDownload from './download.style';

const renderContent = (headline, description, file) => {
  const title = headline ? headline : file.title;

  return (
    <>
      <Headline tag="p" subline className="download__headline">
        {title}
      </Headline>
      {description && (
        <MixedCopy
          fullWidth
          className="download__description"
          dangerouslySetInnerHTML={{
            __html: description
          }}
        />
      )}
    </>
  );
};

const Download = props => {
  const {
    headline,
    file,
    description,
    vertical = false,
    borderless = false,
    showDate = false,
    thumbnail,
    confirmationRequired = false,
    confirmationHeadline,
    confirmationText,
    autoLogout = false
  } = props;
  const t = useTranslations();
  const locale = useLocale();
  const { src, dateCreated, filesize, caption } = file;
  const hasImage = thumbnail;

  const month = {
    de: {
      '01': 'Jan',
      '02': 'Feb',
      '03': 'Mär',
      '04': 'Apr',
      '05': 'Mai',
      '06': 'Jun',
      '07': 'Jul',
      '08': 'Aug',
      '09': 'Sept',
      10: 'Okt',
      11: 'Nov',
      12: 'Dez'
    },
    en: {
      '01': 'Jan',
      '02': 'Feb',
      '03': 'Mar',
      '04': 'Apr',
      '05': 'May',
      '06': 'Jun',
      '07': 'Jul',
      0: 'Aug',
      '09': 'Sept',
      10: 'Oct',
      11: 'Nov',
      12: 'Dec'
    }
  };

  const confirmationLayer = confirmationRequired ? React.createRef() : null;

  const handleDownload = e => {
    if (confirmationRequired) {
      e.preventDefault();
      confirmationLayer.current.openLayer();
    } else {
      track(['trackEvent', 'Download', 'Click', headline]);
    }
  };

  const handleAcceptClick = () => {
    // We need a timeout for edge browser, otherwise it wont download
    setTimeout(() => {
      track(['trackEvent', 'Download', 'Click', headline]);
      confirmationLayer.current.closeLayer();
    }, 200);
  };

  console.log('styledDownload', file, src);
  return (
    <>
      <StyledDownload
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Download: ${headline}`}
        download
        vertical={vertical}
        borderless={borderless}
        hasDate={showDate}
        hasImage={hasImage}
        {...omit(props, ['file', 'headline', 'description', 'thumbnail', 'vertical', 'showDate'])}
        onClick={e => handleDownload(e)}
        onKeyDown={e => (e.key === 'Enter' ? handleDownload(e) : '')}
        className={autoLogout ? 'disabled' : ''}
      >
        {showDate && (
          <div className="download__date">
            <span>{dateCreated.d}</span>
            <span>{month[locale][dateCreated.m]} </span>
            <span>{dateCreated.Y}</span>
          </div>
        )}

        {hasImage && (
          <div className="download__image">
            <Image
              {...thumbnail}
              alt={thumbnail.alt || headline || caption}
              sizes={[
                {
                  width: '100px'
                }
              ]}
            />
          </div>
        )}

        {vertical ? (
          renderContent(headline, description, file)
        ) : (
          <div className="download__content">{renderContent(headline, description, file)}</div>
        )}

        <div className="download__icon">
          <ArrowDown height="2" width="17" />

          {filesize ? (
            <span>
              {getFileType(src)}, {getFileSize(filesize)}
            </span>
          ) : (
            'Download'
          )}
        </div>
      </StyledDownload>

      {confirmationRequired && (
        <ConfirmationLayer
          confirmationHeadline={confirmationHeadline}
          confirmationText={confirmationText}
          acceptButton={
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Download: ${headline}`}
              download
              onClick={() => handleAcceptClick()}
              onKeyDown={e => (e.key === 'Enter' ? handleAcceptClick() : '')}
              className="download__accept-button"
            >
              <LongArrowRight role="presentation" />
              {t('download.accept')}
            </a>
          }
          ref={confirmationLayer}
        />
      )}
    </>
  );
};

Download.propTypes = {
  file: PropTypes.shape({
    src: PropTypes.string,
    filesize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateCreated: PropTypes.shape({
      d: PropTypes.string,
      m: PropTypes.string,
      Y: PropTypes.string
    }),
    title: PropTypes.string,
    assetDesc: PropTypes.string,
    caption: PropTypes.string
  }).isRequired,
  thumbnail: PropTypes.shape(),
  headline: PropTypes.string,
  description: PropTypes.string,
  vertical: PropTypes.bool,
  borderless: PropTypes.bool,
  showDate: PropTypes.bool,
  confirmationRequired: PropTypes.bool,
  confirmationHeadline: PropTypes.string,
  confirmationText: PropTypes.string,
  autoLogout: PropTypes.bool
};

const mapStateToProps = state => ({
  autoLogout: state.token.autoLogout
});

export default connect(mapStateToProps)(Download);
